<template>
  <div class="patient-journey">
    <el-row type="flex" justify="center" :gutter="20">
      <div
        v-for="(metric, idx) in metrics"
        class="metric"
        :key="'metric' + idx"
        @click="filterPatient(metric)"
        :class="{
          'metric-danger': metric.name === 'High Priority Alert',
          'metric-warning': metric.name === 'Normal Priority Alert',
          'metric-info': metric.name === 'Low Priority Alert',
        }"
      >
        <div class="metric-value">{{ metric.value }}</div>
        <div class="metric-name">{{ metric.name }}</div>
      </div>
    </el-row>
    <TabContactsHeader
      @refetch-contact="fetchContacts"
      @apply-filter="onApplyFilter"
      @apply-sort="onApplySort"
      @apply-filter-date="onApplyFilterDate"
      @refetch-data="fetchContacts"
    />
    <el-table v-loading="loadingData" :data="patientsData" style="width: 100%">
      <el-table-column prop="name" label="Name"></el-table-column>
      <el-table-column prop="channel" label="Channel"></el-table-column>
      <el-table-column prop="primaryContact" label="Primary Contact"></el-table-column>
      <el-table-column prop="attributes.project_id" label="Project">
        <template slot-scope="props">
          <span>
            {{ projectName(props.row.attributes.project_id) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="stage.name" label="Milestone"></el-table-column>
      <el-table-column prop="alert" label="Alert" width="250">
        <template slot-scope="props">
          <div
            v-if="props.row.attributes.alert_priority"
            class="alert-card"
            :class="{
              'alert-danger': props.row.attributes.alert_priority === 'High',
              'alert-warning': props.row.attributes.alert_priority === 'Normal',
              'alert-info': props.row.attributes.alert_priority === 'Low',
            }"
          >
            <div style="margin-bottom: 10px">
              <strong>{{ props.row.attributes.alert_type }}</strong>
            </div>
            <div style="margin-bottom: 10px">
              {{ props.row.attributes.alert_label }}:
              <strong>{{ props.row.attributes.alert_value }}</strong>
            </div>
            <div style="display: flex">
              <span style="width: 85px">Status: </span>
              <el-select
                v-model="props.row.attributes.alert_status"
                placeholder="Select"
                size="mini"
                @change="editAlert(props.row)"
              >
                <el-option v-for="item in status" :key="item" :label="item" :value="item">
                </el-option>
              </el-select>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="actions" label="Actions">
        <template slot-scope="props">
          <el-button
            icon="el-icon-info"
            circle
            type="info"
            size="mini"
            @click="viewPatient(props.row)"
          >
          </el-button>
          <el-button
            icon="el-icon-edit"
            circle
            type="warning"
            size="mini"
            @click="editPatient(props.row)"
          >
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      background
      layout="prev, pager, next"
      :page-size="pageSize"
      :total="patientsCount"
      @current-change="setPage"
      style="padding: 10px 0"
    >
    </el-pagination>

    <el-dialog title="Patient" :visible.sync="patientDialog">
      <div v-if="patient.id">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-steps :active="milestone" direction="vertical" :space="65">
              <el-step title="Start"></el-step>
              <el-step v-for="(item, index) in milestones" :title="item.name" :key="'ml' + item.id">
                <div slot="description" v-if="index < milestone">
                  <a href="#">View details</a>
                </div>
              </el-step>
              <el-step title="End"></el-step>
            </el-steps>
          </el-col>
          <el-col :span="12">
            <div style="margin-bottom: 15px">
              <label>Name</label>
              <br />
              <strong>{{ patient.name }}</strong>
            </div>
            <div style="margin-bottom: 15px">
              <label>Contact</label>
              <br />
              <strong>{{ patient.primaryContact }} ({{ patient.channel }})</strong>
            </div>
            <div style="margin-bottom: 15px">
              <label>Project</label>
              <br />
              <strong>{{ projectName(patient.attributes.project_id) }}</strong>
            </div>
            <div style="margin-bottom: 15px">
              <label>Notes</label>
              <br />
              <el-input
                type="textarea"
                :rows="7"
                placeholder="Please input"
                v-model="patient.attributes.notes"
              >
              </el-input>
              <div style="text-align: right; margin-top: 15px">
                <el-button size="small" type="success" @click="savePatient">Save</el-button>
              </div>
            </div>
            <div
              v-if="patient.attributes.alert_priority"
              class="alert-card"
              :class="{
                'alert-danger': patient.attributes.alert_priority === 'High',
                'alert-warning': patient.attributes.alert_priority === 'Normal',
                'alert-info': patient.attributes.alert_priority === 'Low',
              }"
            >
              <div style="margin-bottom: 10px">
                <strong>{{ patient.attributes.alert_type }}</strong>
              </div>
              <div style="margin-bottom: 10px">
                {{ patient.attributes.alert_label }}:
                <strong>{{ patient.attributes.alert_value }}</strong>
              </div>
              <div style="display: flex">
                <span style="width: 85px">Status: </span>
                <el-select
                  v-model="patient.attributes.alert_status"
                  placeholder="Select"
                  size="mini"
                  @change="editAlert(patient)"
                >
                  <el-option v-for="item in status" :key="item" :label="item" :value="item">
                  </el-option>
                </el-select>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="patientDialog = false">Close</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Patient" :visible.sync="editDialog" width="40%">
      <div v-if="patient.id">
        <div style="margin-bottom: 20px">
          <label>First Name</label>
          <el-input v-model="patient.firstName"></el-input>
        </div>
        <div style="margin-bottom: 20px">
          <label>Last Name</label>
          <el-input v-model="patient.lastName"></el-input>
        </div>
        <div style="margin-bottom: 20px">
          <label>Channel</label>
          <el-select v-model="patient.channel" placeholder="Select" style="width: 100%">
            <el-option v-for="item in channels" :key="item" :label="item" :value="item">
            </el-option>
          </el-select>
        </div>
        <div style="margin-bottom: 20px">
          <label>Primary Contact</label>
          <el-input v-model="patient.primaryContact"></el-input>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialog = false">Cancel</el-button>
        <el-button type="primary" @click="savePatient">Save</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Alert" :visible.sync="alertDialog" width="40%">
      <div v-if="patient.id">
        <div style="margin-bottom: 20px">
          <label>Name</label>
          <br />
          <strong>{{ patient.name }}</strong>
        </div>
        <div style="margin-bottom: 20px">
          <label>Alert Priority</label>
          <br />
          <el-tag
            size="small"
            effect="dark"
            type="danger"
            v-if="patient.attributes.alert_priority === 'High'"
          >
            {{ patient.attributes.alert_priority }}
          </el-tag>
          <el-tag
            size="small"
            type="danger"
            v-else-if="patient.attributes.alert_priority === 'Normal'"
          >
            {{ patient.attributes.alert_priority }}
          </el-tag>
          <el-tag size="small" type="warning" v-if="patient.attributes.alert_priority === 'Low'">
            {{ patient.attributes.alert_priority }}
          </el-tag>
        </div>
        <div style="margin-bottom: 20px">
          <label>Alert Type</label>
          <br />
          <strong>{{ patient.attributes.alert_type }}</strong>
        </div>
        <div style="margin-bottom: 20px">
          <label>Alert Status</label>
          <el-select
            v-model="patient.attributes.alert_status"
            placeholder="Select"
            style="width: 100%"
          >
            <el-option v-for="item in status" :key="item" :label="item" :value="item"> </el-option>
          </el-select>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="alertDialog = false">Cancel</el-button>
        <el-button type="primary" @click="savePatient">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { gql } from "@apollo/client/core";
import moment from "moment";
import _ from "lodash";

import TabContactsHeader from "@/views/Outreach/TabContacts/TabContactsHeader.vue";

export default {
  components: {
    TabContactsHeader,
  },
  data() {
    return {
      journeyList: [],
      patient: {
        id: 0,
      },
      filterDate: [],
      filterBoard: [],
      sortBoard: [],
      channels: ["Whatsapp", "Email", "SMS", "Webchat"],
      status: ["new", "reviewed"],
      pageSize: 10,
      loadingData: false,
      patientDialog: false,
      editDialog: false,
      alertDialog: false,
    };
  },
  computed: {
    patientsData() {
      const raw = this.$store.state.outreach.contacts.data || [];
      const data = _.map(raw, (item) => {
        const attributes = _.reduce(
          item.contactattributes,
          (result, value) => {
            result[value.name] = value.contactattributevalue.value;
            return result;
          },
          {}
        );
        return {
          ...item,
          attributes: attributes,
        };
      });

      return data;
    },
    patientsCount() {
      return this.$store.state.outreach.contacts.count || 0;
    },
    metrics() {
      const raw = this.$store.state.outreach.metrics || [];
      const data = [];

      _.each(raw, (item) => {
        if (
          item.name === "All patients" ||
          item.name === "High Priority Alert" ||
          item.name === "Normal Priority Alert" ||
          item.name === "Low Priority Alert"
        ) {
          data.push(item);
        }
      });
      return data;
    },
    milestone() {
      const item = _.find(this.journeyList, {
        id: this.patient.attributes.project_id,
      });
      if (item) {
        const index = _.findIndex(item.milestones, {
          name: this.patient.stage.name,
        });
        return index + 1;
      } else {
        return -1;
      }
    },
    windowHeight() {
      return window.innerHeight;
    },
    tableHeight() {
      let height = window.innerHeight;
      return height - 425;
    },
  },
  mounted() {
    this.init();
    this.getJourney();
    this.filterDate = {
      startDate: moment().subtract(1, "years"),
      endDate: moment(),
    };
  },
};
</script>

<style>
.alert-title .el-icon-search {
  font-weight: bold;
  color: #303133;
}
</style>
<style scoped>
.patient-journey {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.metric {
  padding: 20px;
  border: 1px solid #409eff;
  background-color: #d9ecff;
  height: 70px;
  width: 150px;
  cursor: pointer;
}

.metric-value {
  font-size: 32px;
}

.metric-name {
  color: grey;
}

.alert-card {
  padding: 20px;
}

.alert-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.alert-title span {
  font-size: 18px;
  font-weight: bold;
}

.metric-danger,
.alert-danger {
  background-color: #fde2e2;
  border: 1px solid #c45656;
}

.metric-warning,
.alert-warning {
  background-color: #f8e3c5;
  border: 1px solid #b88230;
}

.metric-info,
.alert-info {
  background-color: #fdf6ec;
  border: 1px solid #e6a23c;
}

.patient-number {
  font-size: 30px;
  font-weight: bold;
  text-align: center;
}
</style>
